import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState } from "react";
import { Search, Menu, X, Rss, ArrowLeft, Home, BookOpen, Tag } from "lucide-react";
import BrandMark from "@/components/BrandMark";
import ThemeToggle from "@/components/ThemeToggle";
import { getCategories } from "@/lib/blog/store";

export default function BlogHeader() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const path = usePathname();
  const categories = getCategories();

  const NavLink = ({ to, children }: { to: string; children: React.ReactNode }) => (
    <Link
      href={to}
      onClick={() => setOpen(false)}
      className={`text-sm font-bold transition-colors ${
        path === to ? "text-primary-main" : "text-muted-foreground hover:text-foreground"
      }`}
    >
      {children}
    </Link>
  );

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background/85 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6">
        <div className="flex items-center gap-3 lg:gap-8">
          <Link href="/" className="flex items-center gap-2.5">
            <BrandMark size={34} />
            <div className="hidden flex-col leading-none sm:flex">
              <span className="text-[13px] font-black tracking-tight text-foreground">
                ترندی اسمارت
              </span>
              <span className="mt-0.5 text-[9px] font-bold tracking-[0.2em] text-primary-main">
                BLOG
              </span>
            </div>
          </Link>
          <nav className="hidden items-center gap-5 lg:flex">
            <NavLink to="/blog">همه مقالات</NavLink>
            {categories.slice(0, 4).map((c) => (
              <Link
                key={c.id}
                href={`/blog/category/${c.slug}`}
                className={`text-sm font-bold transition-colors ${
                  path.includes(`/blog/category/${c.slug}`)
                    ? "text-primary-main"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {c.name}
              </Link>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative hidden md:block">
            <Search
              size={14}
              className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="جست‌وجوی مقاله..."
              className="h-9 w-56 rounded-full border border-border bg-muted/40 pr-9 pl-3 text-xs outline-none transition-colors focus:border-primary-main focus:bg-background"
            />
          </div>
          <ThemeToggle variant="floating" />
          <Link
            href="/"
            className="hidden items-center gap-1 rounded-lg bg-primary-main px-3 py-2 text-[12px] font-black text-white transition-colors hover:bg-primary-400 md:inline-flex"
          >
            <Home size={12} />
            بازگشت به سایت
          </Link>
          <button
            onClick={() => setOpen((v) => !v)}
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-border lg:hidden"
            aria-label="منو"
          >
            {open ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="border-t border-border bg-background lg:hidden">
          <div className="mx-auto max-w-7xl space-y-3 px-4 py-4">
            <div className="relative">
              <Search
                size={14}
                className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="جست‌وجوی مقاله..."
                className="h-10 w-full rounded-xl border border-border bg-muted/40 pr-9 pl-3 text-sm outline-none focus:border-primary-main focus:bg-background"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Link
                href="/blog"
                onClick={() => setOpen(false)}
                className="flex items-center justify-between rounded-xl border border-border bg-card px-3 py-2.5 text-sm font-bold text-foreground"
              >
                <span className="flex items-center gap-2">
                  <BookOpen size={14} className="text-primary-main" />
                  همه مقالات
                </span>
                <ArrowLeft size={14} className="text-muted-foreground" />
              </Link>
              {categories.map((c) => (
                <Link
                  key={c.id}
                  href={`/blog/category/${c.slug}`}
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-between rounded-xl border border-border bg-card px-3 py-2.5 text-sm font-bold text-foreground"
                >
                  <span className="flex items-center gap-2">
                    <Tag size={13} className={c.color} />
                    {c.name}
                  </span>
                  <ArrowLeft size={14} className="text-muted-foreground" />
                </Link>
              ))}
            </div>
            <Link
              href="/"
              onClick={() => setOpen(false)}
              className="flex w-full items-center justify-center gap-1.5 rounded-xl bg-primary-main py-3 text-sm font-black text-white"
            >
              <Home size={14} />
              بازگشت به سایت اصلی
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
